import React from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Bell, CheckCheck } from 'lucide-react';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import {
  dismissNotification,
  markAllNotificationsRead,
  markNotificationRead,
} from '@/store/actions/notifications';
import type { NotificationItem as NotificationItemType, NotificationsState } from './types';
import { resolveNotificationRoute } from './notificationRouting';
import NotificationItem from './NotificationItem';

interface NotificationsPopoverProps {
  onNavigate?: () => void;
}

const NotificationsPopover: React.FC<NotificationsPopoverProps> = ({ onNavigate }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { items, unreadCount, loading } = useSelector(
    (state: any) => state.notifications as NotificationsState
  );

  const handleClick = (notification: NotificationItemType) => {
    if (!notification.isRead) {
      dispatch(markNotificationRead(notification.id));
    }
    const route = resolveNotificationRoute(notification);
    if (route) {
      history.push(route);
      onNavigate?.();
    }
  };

  const handleDismiss = (id: string) => {
    dispatch(dismissNotification(id));
  };

  return (
    <div className="flex flex-col max-h-[70vh]" data-testid="notifications-popover">
      <div className="flex items-center justify-between border-b border-neutral-200 px-3 py-2.5">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-neutral-900">Notifikace</span>
          {unreadCount > 0 && (
            <span className="rounded-full bg-primary-600 px-1.5 py-0.5 text-[10px] font-medium leading-none text-white">
              {unreadCount}
            </span>
          )}
        </div>
        <button
          type="button"
          data-testid="notifications-mark-all-read-btn"
          disabled={unreadCount === 0}
          onClick={() => dispatch(markAllNotificationsRead())}
          className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 disabled:cursor-not-allowed disabled:text-neutral-300"
        >
          <CheckCheck size={13} />
          Označit vše
        </button>
      </div>

      <div className="min-h-[80px] flex-1 overflow-y-auto divide-y divide-neutral-100">
        {loading && items.length === 0 ? (
          <div className="flex justify-center py-8">
            <Spinner size="sm" />
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            compact
            icon={<Bell size={18} />}
            title="Žádné notifikace"
            description="Nové notifikace se zobrazí zde."
          />
        ) : (
          items.map((notification) => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onClick={handleClick}
              onDismiss={handleDismiss}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default NotificationsPopover;
